import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../components/Messages';
import { showApiError } from '../../utils/errorUtils';
import { hasRole } from '../../utils/roleUtils';
import '../Admin/Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewExtrusionWorkOrder = forwardRef(({
  orderId,
  editMode = false,
  createMode = false,
  onBack,
  onSave,
  onCancel,
  onOrderCreated,
  onStatusLoaded,
}, ref) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const { showSuccess, showError } = useMessages();

  const [activeTab, setActiveTab] = useState('general');
  const [form, setForm] = useState({
    bobbin_item_id: '',
    thickness_min: '',
    thickness_max: '',
    width_mm: '',
    target_length_m: '',
    planned_bobbins: '1',
    estimated_weight_kg: '',
    temperature_min: '',
    temperature_max: '',
    line_speed_mpm: '',
    former_config: '',
    production_date: '',
    notes: '',
  });
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Material requisition state
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [materialForm, setMaterialForm] = useState({
    inventory_item_id: '',
    material_type: 'PELLET',
    requested_quantity_kg: '',
    location_from: '',
    location_to: '',
  });

  // Consumption log state
  const [showAddConsumption, setShowAddConsumption] = useState(false);
  const [consumptionForm, setConsumptionForm] = useState({
    inventory_item_id: '',
    material_type: 'PELLET',
    bags_used: '0',
    kg_consumed: '',
    notes: '',
  });

  // Bobbin record state
  const [showAddBobbin, setShowAddBobbin] = useState(false);
  const [bobbinForm, setBobbinForm] = useState({
    bobbin_barcode: '',
    meters_produced: '',
    weight_kg: '',
    classification: 'GOOD',
    notes: '',
  });
  const [editingBobbinId, setEditingBobbinId] = useState(null);
  const [editBobbinForm, setEditBobbinForm] = useState({});

  // Waste recording state
  const [wasteForm, setWasteForm] = useState({ total_waste_kg: '', waste_notes: '' });

  // Inventory entry state
  const [inventoryEntryForm, setInventoryEntryForm] = useState({ destination_location: '', notes: '' });
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  // Void state
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  // Lookups
  const [inventoryItems, setInventoryItems] = useState([]);
  const [bobbinSearchText, setBobbinSearchText] = useState('');
  const [materialSearchText, setMaterialSearchText] = useState('');
  const [consumptionSearchText, setConsumptionSearchText] = useState('');

  useImperativeHandle(ref, () => ({
    handleSave,
  }));

  useEffect(() => {
    fetchInventoryItems();
    if (orderId && !createMode) {
      fetchOrder();
    }
  }, [orderId, createMode]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}`));
      if (res.ok) {
        const data = await res.json();
        setOrderData(data);
        setForm({
          bobbin_item_id: data.bobbin_item_id || '',
          thickness_min: data.thickness_min || '',
          thickness_max: data.thickness_max || '',
          width_mm: data.width_mm || '',
          target_length_m: data.target_length_m || '',
          planned_bobbins: data.planned_bobbins || '1',
          estimated_weight_kg: data.estimated_weight_kg || '',
          temperature_min: data.temperature_min || '',
          temperature_max: data.temperature_max || '',
          line_speed_mpm: data.line_speed_mpm || '',
          former_config: data.former_config || '',
          production_date: data.production_date || '',
          notes: data.notes || '',
        });
        if (data.bobbin_item_name) {
          setBobbinSearchText(`${data.bobbin_item_name} (${data.bobbin_item_code})`);
        }
        // Pre-fill waste form if already recorded
        if (data.waste_recorded) {
          setWasteForm({
            total_waste_kg: data.total_waste_kg || '',
            waste_notes: data.waste_notes || '',
          });
        }
        if (onStatusLoaded) onStatusLoaded(data.status);
      }
    } catch (e) {
      console.error('Error fetching order:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      // The list endpoint is paginated (max 500 per page): load every page.
      const items = [];
      for (let page = 1; ; page++) {
        const res = await apiFetch(getApiUrl(`/api/inventory?page_size=500&page=${page}`));
        if (!res.ok) break;
        const data = await res.json();
        const pageItems = data.items || [];
        items.push(...pageItems);
        if (pageItems.length === 0 || items.length >= (data.total || 0)) break;
      }
      setInventoryItems(items);
    } catch (e) {
      console.error('Error fetching inventory items:', e);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (saving) return;
    if (!form.bobbin_item_id) {
      showError(t('viewExtrusionWorkOrder.toasts.selectBobbinSku'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        bobbin_item_id: parseInt(form.bobbin_item_id),
        thickness_min: form.thickness_min ? parseFloat(form.thickness_min) : null,
        thickness_max: form.thickness_max ? parseFloat(form.thickness_max) : null,
        width_mm: form.width_mm ? parseFloat(form.width_mm) : null,
        target_length_m: form.target_length_m ? parseFloat(form.target_length_m) : null,
        planned_bobbins: parseInt(form.planned_bobbins) || 1,
        estimated_weight_kg: form.estimated_weight_kg ? parseFloat(form.estimated_weight_kg) : null,
        temperature_min: form.temperature_min ? parseFloat(form.temperature_min) : null,
        temperature_max: form.temperature_max ? parseFloat(form.temperature_max) : null,
        line_speed_mpm: form.line_speed_mpm ? parseFloat(form.line_speed_mpm) : null,
        former_config: form.former_config || null,
        production_date: form.production_date || null,
        notes: form.notes || null,
      };

      let res;
      if (createMode) {
        res = await apiFetch(getApiUrl('/api/extrusions/orders'), {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else {
        res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}`), {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        const data = await res.json();
        showSuccess(createMode ? t('viewExtrusionWorkOrder.toasts.created') : t('viewExtrusionWorkOrder.toasts.updated'));
        if (createMode && onOrderCreated) {
          onOrderCreated(data.work_order_id);
        } else if (onSave) {
          onSave();
        }
      } else {
        await showApiError(res, showError, tCommon);
        return;
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.saveError'));
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  // --- Action handlers ---

  const handleApprove = async () => {
    if (!window.confirm(t('viewExtrusionWorkOrder.confirm.approve'))) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}/approve`), { method: 'POST' });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.approved'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.approveError'));
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm(t('viewExtrusionWorkOrder.confirm.cancel'))) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}/cancel`), { method: 'POST' });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.cancelled'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.cancelError'));
    }
  };

  const handleVoid = async () => {
    if (voiding || voidReason.trim().length < 5) return;
    setVoiding(true);
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}/void`), {
        method: 'POST',
        body: JSON.stringify({ reason: voidReason.trim() }),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.voided'));
        setShowVoidModal(false);
        setVoidReason('');
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.voidError'));
    } finally {
      setVoiding(false);
    }
  };

  const handleCompleteOrder = async () => {
    if (!window.confirm(t('viewExtrusionWorkOrder.confirm.complete'))) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}/complete`), { method: 'POST' });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.completed'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.completeError'));
    }
  };

  // --- Material requisition handlers ---

  const handleAddMaterial = async () => {
    if (!materialForm.inventory_item_id || !materialForm.requested_quantity_kg) {
      showError(t('viewExtrusionWorkOrder.toasts.fillMaterialQty'));
      return;
    }
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}/materials`), {
        method: 'POST',
        body: JSON.stringify({
          inventory_item_id: parseInt(materialForm.inventory_item_id),
          material_type: materialForm.material_type,
          requested_quantity_kg: parseFloat(materialForm.requested_quantity_kg),
          location_from: materialForm.location_from || undefined,
          location_to: materialForm.location_to || null,
        }),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.materialAdded'));
        setShowAddMaterial(false);
        setMaterialForm({ inventory_item_id: '', material_type: 'PELLET', requested_quantity_kg: '', location_from: '', location_to: '' });
        setMaterialSearchText('');
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.materialAddError'));
    }
  };

  const handleApproveMaterial = async (itemId) => {
    const mat = orderData?.materials?.find(m => m.requisition_item_id === itemId);
    if (!mat) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/materials/${itemId}/approve`), {
        method: 'PUT',
        body: JSON.stringify({ approved_quantity_kg: parseFloat(mat.requested_quantity_kg) }),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.materialApproved'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.materialApproveError'));
    }
  };

  const handleCancelMaterial = async (itemId) => {
    if (!window.confirm(t('viewExtrusionWorkOrder.confirm.cancelMaterial'))) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/materials/${itemId}/cancel`), { method: 'PUT' });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.materialCancelled'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.materialCancelError'));
    }
  };

  const handleIssueMaterial = async (itemId) => {
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/materials/${itemId}/issue`), {
        method: 'PUT',
        body: JSON.stringify({}),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.materialIssued'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.materialIssueError'));
    }
  };

  // --- Consumption log handlers ---

  const handleAddConsumption = async () => {
    if (!consumptionForm.inventory_item_id || !consumptionForm.kg_consumed) {
      showError(t('viewExtrusionWorkOrder.toasts.fillMaterialKg'));
      return;
    }
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}/consumption`), {
        method: 'POST',
        body: JSON.stringify({
          inventory_item_id: parseInt(consumptionForm.inventory_item_id),
          material_type: consumptionForm.material_type,
          bags_used: parseInt(consumptionForm.bags_used) || 0,
          kg_consumed: parseFloat(consumptionForm.kg_consumed),
          notes: consumptionForm.notes || null,
        }),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.consumptionRecorded'));
        setShowAddConsumption(false);
        setConsumptionForm({ inventory_item_id: '', material_type: 'PELLET', bags_used: '0', kg_consumed: '', notes: '' });
        setConsumptionSearchText('');
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.consumptionError'));
    }
  };

  const handleDeleteConsumption = async (logId) => {
    if (!window.confirm(t('viewExtrusionWorkOrder.confirm.deleteConsumption'))) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/consumption/${logId}`), { method: 'DELETE' });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.entryDeleted'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.entryDeleteError'));
    }
  };

  // --- Bobbin record handlers ---

  const handleAddBobbin = async () => {
    if (!bobbinForm.weight_kg) {
      showError(t('viewExtrusionWorkOrder.toasts.enterWeight'));
      return;
    }
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}/bobbins`), {
        method: 'POST',
        body: JSON.stringify({
          bobbin_barcode: bobbinForm.bobbin_barcode || null,
          meters_produced: bobbinForm.meters_produced ? parseFloat(bobbinForm.meters_produced) : null,
          weight_kg: parseFloat(bobbinForm.weight_kg),
          classification: bobbinForm.classification,
          notes: bobbinForm.notes || null,
        }),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.bobbinRecorded'));
        setShowAddBobbin(false);
        setBobbinForm({ bobbin_barcode: '', meters_produced: '', weight_kg: '', classification: 'GOOD', notes: '' });
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.bobbinAddError'));
    }
  };

  const handleStartEditBobbin = (b) => {
    setEditingBobbinId(b.bobbin_record_id);
    setEditBobbinForm({
      bobbin_barcode: b.bobbin_barcode || '',
      meters_produced: b.meters_produced || '',
      weight_kg: b.weight_kg || '',
      classification: b.classification || 'GOOD',
      notes: b.notes || '',
    });
  };

  const handleSaveEditBobbin = async () => {
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/bobbins/${editingBobbinId}`), {
        method: 'PUT',
        body: JSON.stringify({
          bobbin_barcode: editBobbinForm.bobbin_barcode || null,
          meters_produced: editBobbinForm.meters_produced ? parseFloat(editBobbinForm.meters_produced) : null,
          weight_kg: editBobbinForm.weight_kg ? parseFloat(editBobbinForm.weight_kg) : null,
          classification: editBobbinForm.classification,
          notes: editBobbinForm.notes || null,
        }),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.bobbinUpdated'));
        setEditingBobbinId(null);
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.bobbinUpdateError'));
    }
  };

  const handleDeleteBobbin = async (bobbinId) => {
    if (!window.confirm(t('viewExtrusionWorkOrder.confirm.deleteBobbin'))) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/bobbins/${bobbinId}`), { method: 'DELETE' });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.bobbinDeleted'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.bobbinDeleteError'));
    }
  };

  // --- Waste recording handler ---

  const handleRecordWaste = async () => {
    if (!wasteForm.total_waste_kg && wasteForm.total_waste_kg !== 0) {
      showError(t('viewExtrusionWorkOrder.toasts.enterWaste'));
      return;
    }
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}/record-waste`), {
        method: 'POST',
        body: JSON.stringify({
          total_waste_kg: parseFloat(wasteForm.total_waste_kg),
          waste_notes: wasteForm.waste_notes || null,
        }),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.wasteRecorded'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.wasteError'));
    }
  };

  // --- Inventory entry handlers ---

  const handleRequestInventoryEntry = async () => {
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/orders/${orderId}/inventory-entry`), {
        method: 'POST',
        body: JSON.stringify({
          destination_location: inventoryEntryForm.destination_location || undefined,
          notes: inventoryEntryForm.notes || null,
        }),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.entryRequested'));
        setInventoryEntryForm({ destination_location: '', notes: '' });
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.entryRequestError'));
    }
  };

  const handleApproveInventoryEntry = async (entryId) => {
    if (!window.confirm(t('viewExtrusionWorkOrder.confirm.approveEntry'))) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/inventory-entries/${entryId}/approve`), { method: 'POST' });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.entryApproved'));
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.entryApproveError'));
    }
  };

  const handleRejectInventoryEntry = async (entryId) => {
    if (!rejectionReason.trim()) {
      showError(t('viewExtrusionWorkOrder.toasts.enterRejectionReason'));
      return;
    }
    try {
      const res = await apiFetch(getApiUrl(`/api/extrusions/inventory-entries/${entryId}/reject`), {
        method: 'POST',
        body: JSON.stringify({ rejection_reason: rejectionReason }),
      });
      if (res.ok) {
        showSuccess(t('viewExtrusionWorkOrder.toasts.entryRejected'));
        setShowRejectForm(false);
        setRejectionReason('');
        fetchOrder();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewExtrusionWorkOrder.toasts.entryRejectError'));
    }
  };

  // --- Helpers ---

  const getStatusBadge = (status) => {
    const map = {
      DRAFT: 'status-warning',
      APPROVED: 'status-info',
      MATERIALS_REQUESTED: 'status-info',
      IN_PROGRESS: 'status-info',
      COMPLETED: 'status-success',
      CANCELLED: 'status-error',
      VOIDED: 'status-error',
    };
    return <span className={`status-badge ${map[status] || 'status-inactive'}`}>{status ? t('extrusionWorkOrders.status.' + status, { defaultValue: status.replace(/_/g, ' ') }) : status}</span>;
  };

  const getMaterialStatusBadge = (status) => {
    const map = {
      PENDING: 'status-warning',
      APPROVED: 'status-info',
      ISSUED: 'status-success',
      CANCELLED: 'status-error',
    };
    return <span className={`status-badge ${map[status] || 'status-inactive'}`}>{status ? t('viewExtrusionWorkOrder.materialStatus.' + status, { defaultValue: status }) : status}</span>;
  };

  const formatDate = (val) => {
    if (!val) return '-';
    return formatAppDate(val);
  };

  const formatDateTime = (val) => {
    if (!val) return '-';
    return formatAppDate(val, 'datetime');
  };

  const formatNum = (val, decimals = 2) => {
    if (val == null || val === '') return '-';
    return parseFloat(val).toFixed(decimals);
  };

  const formatCurrency = (val) => val != null ? `$${parseFloat(val).toFixed(4)}` : '-';

  const status = orderData?.status || 'DRAFT';
  const isEditing = editMode || createMode;
  const canApprove = hasRole(user, 'ADMIN', 'SUPERVISOR') && status === 'DRAFT';
  const canCancel = hasRole(user, 'ADMIN', 'MANAGER') && (status === 'DRAFT' || status === 'APPROVED');
  const canAddMaterials = ['DRAFT', 'APPROVED', 'MATERIALS_REQUESTED', 'IN_PROGRESS'].includes(status);
  const canManageMaterials = hasRole(user, 'ADMIN', 'WAREHOUSE');
  const canAddConsumption = status === 'IN_PROGRESS';
  const canAddBobbins = status === 'IN_PROGRESS';
  const canComplete = hasRole(user, 'ADMIN', 'SUPERVISOR') && status === 'IN_PROGRESS';
  const isCompleted = status === 'COMPLETED';
  const canManageInventoryEntry = hasRole(user, 'ADMIN', 'WAREHOUSE');
  const inventoryEntry = orderData?.inventory_entry || null;

  const materials = orderData?.materials || [];
  const consumption = orderData?.consumption || [];
  const bobbins = orderData?.bobbins || [];

  // Void: SUPERVISOR/MANAGER/ADMIN, for orders Cancel no longer covers (or that this user cannot cancel)
  const canVoid = hasRole(user, 'ADMIN', 'SUPERVISOR', 'MANAGER') && !canCancel && !['CANCELLED', 'VOIDED'].includes(status);
  const voidBlocker = inventoryEntry?.status === 'APPROVED' ? t('viewExtrusionWorkOrder.void.blockerEntered')
    : inventoryEntry?.status === 'PENDING' ? t('viewExtrusionWorkOrder.void.blockerPending')
    : consumption.length > 0 ? t('viewExtrusionWorkOrder.void.blockerConsumption')
    : null;

  if (loading) {
    return <div className="view-container"><div className="loading">{t('viewExtrusionWorkOrder.loading')}</div></div>;
  }

  // --- Render searchable input helper ---
  const renderSearchableInput = (listId, searchText, setSearchText, onSelect, placeholder, items) => (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        list={`inv-list-${listId}`}
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
          const match = items.find(i =>
            `${i.item_name} (${i.item_code})` === e.target.value
          );
          if (match) {
            onSelect(match.item_id);
          } else {
            onSelect('');
          }
        }}
        placeholder={placeholder}
        style={{ width: '100%' }}
      />
      <datalist id={`inv-list-${listId}`}>
        {items.map(i => (
          <option key={i.item_id} value={`${i.item_name} (${i.item_code})`} />
        ))}
      </datalist>
    </div>
  );

  // Tab styling
  const tabStyle = (tab) => ({
    padding: '8px 20px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: activeTab === tab ? '600' : '400',
    background: activeTab === tab ? 'var(--bg-primary)' : 'transparent',
    color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-secondary)',
    boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
  });

  // Summary card helper
  const SummaryCard = ({ label, value, unit, color }) => (
    <div style={{
      padding: '12px 16px',
      background: 'var(--bg-secondary)',
      borderRadius: '8px',
      border: '1px solid var(--border-color)',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '20px', fontWeight: '600', color: color || 'var(--text-primary)' }}>
        {value}{unit && <span style={{ fontSize: '12px', fontWeight: '400', marginLeft: '2px' }}>{unit}</span>}
      </div>
    </div>
  );

  return (
    <div className="view-container" style={{ padding: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2>{createMode ? t('viewExtrusionWorkOrder.newTitle') : orderData?.order_number || t('viewExtrusionWorkOrder.workOrder')}</h2>
        {!isEditing && orderId && (
          <div style={{ display: 'flex', gap: '6px' }}>
            {canApprove && (
              <button className="btn-primary" onClick={handleApprove}>
                {t('viewExtrusionWorkOrder.actions.approve')}
              </button>
            )}
            {canComplete && (
              <button className="btn-primary" onClick={handleCompleteOrder}
                disabled={!orderData?.waste_recorded || bobbins.length === 0}
                title={!orderData?.waste_recorded ? t('viewExtrusionWorkOrder.hints.recordWasteFirst') : bobbins.length === 0 ? t('viewExtrusionWorkOrder.hints.addBobbinsFirst') : t('viewExtrusionWorkOrder.hints.completeOrder')}
              >
                {t('viewExtrusionWorkOrder.actions.completeOrder')}
              </button>
            )}
            {canCancel && (
              <button className="btn-danger" onClick={handleCancelOrder}>
                {t('common:ui.cancel')}
              </button>
            )}
            {canVoid && (
              <button className="btn-danger" onClick={() => setShowVoidModal(true)}
                disabled={!!voidBlocker}
                title={voidBlocker || t('viewExtrusionWorkOrder.hints.voidOrder')}
              >
                {t('viewExtrusionWorkOrder.actions.voidOrder')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs (only in view mode with an existing order) */}
      {!createMode && orderId && (
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '6px', padding: '2px', marginBottom: '16px' }}>
          <button onClick={() => setActiveTab('general')} style={tabStyle('general')}>{t('viewExtrusionWorkOrder.tabs.general')}</button>
          <button onClick={() => setActiveTab('materials')} style={tabStyle('materials')}>
            {t('viewExtrusionWorkOrder.tabs.materials')} {materials.length > 0 && `(${materials.length})`}
          </button>
          <button onClick={() => setActiveTab('consumption')} style={tabStyle('consumption')}>
            {t('viewExtrusionWorkOrder.tabs.consumption')} {consumption.length > 0 && `(${consumption.length})`}
          </button>
          <button onClick={() => setActiveTab('bobbins')} style={tabStyle('bobbins')}>
            {t('viewExtrusionWorkOrder.tabs.bobbins')} {bobbins.length > 0 && `(${bobbins.length})`}
          </button>
        </div>
      )}

      {/* === GENERAL TAB === */}
      {(activeTab === 'general' || createMode) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
          {/* Row 1: Bobbin SKU, Status, Production Date */}
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.bobbinSku')}</label>
            {isEditing ? (
              renderSearchableInput(
                'bobbin',
                bobbinSearchText,
                setBobbinSearchText,
                (id) => handleChange('bobbin_item_id', id),
                t('viewExtrusionWorkOrder.placeholders.searchBobbinSku'),
                inventoryItems
              )
            ) : (
              <div className="field-value">
                <strong>{orderData?.bobbin_item_code}</strong> — {orderData?.bobbin_item_name}
              </div>
            )}
          </div>
          {!createMode && (
            <div className="form-group">
              <label>{t('common:ui.status')}</label>
              <div className="field-value">{getStatusBadge(status)}</div>
            </div>
          )}
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.productionDate')}</label>
            {isEditing ? (
              <input type="date" value={form.production_date} onChange={(e) => handleChange('production_date', e.target.value)} />
            ) : (
              <div className="field-value">{formatDate(orderData?.production_date)}</div>
            )}
          </div>

          {/* Row 2: Planned Bobbins, Est. Weight/Bobbin */}
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.plannedBobbins')}</label>
            {isEditing ? (
              <input type="number" min="1" value={form.planned_bobbins} onChange={(e) => handleChange('planned_bobbins', e.target.value)} />
            ) : (
              <div className="field-value">{orderData?.planned_bobbins}</div>
            )}
          </div>
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.estWeightPerBobbin')}</label>
            {isEditing ? (
              <input type="number" step="0.001" value={form.estimated_weight_kg} onChange={(e) => handleChange('estimated_weight_kg', e.target.value)} placeholder={t('common:ui.optional')} />
            ) : (
              <div className="field-value">{formatNum(orderData?.estimated_weight_kg, 3)}</div>
            )}
          </div>
          {isEditing && <div />}

          {/* Row 3: Thickness Range, Width, Target Length */}
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.thicknessRange')}</label>
            {isEditing ? (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input type="number" step="0.001" value={form.thickness_min} onChange={(e) => handleChange('thickness_min', e.target.value)} placeholder={t('viewExtrusionWorkOrder.placeholders.min')} style={{ width: '45%' }} />
                <span>-</span>
                <input type="number" step="0.001" value={form.thickness_max} onChange={(e) => handleChange('thickness_max', e.target.value)} placeholder={t('viewExtrusionWorkOrder.placeholders.max')} style={{ width: '45%' }} />
              </div>
            ) : (
              <div className="field-value">
                {orderData?.thickness_min || orderData?.thickness_max
                  ? `${formatNum(orderData?.thickness_min, 3)} - ${formatNum(orderData?.thickness_max, 3)}`
                  : '-'}
              </div>
            )}
          </div>
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.width')}</label>
            {isEditing ? (
              <input type="number" step="0.01" value={form.width_mm} onChange={(e) => handleChange('width_mm', e.target.value)} placeholder={t('common:ui.optional')} />
            ) : (
              <div className="field-value">{formatNum(orderData?.width_mm)}</div>
            )}
          </div>
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.targetLength')}</label>
            {isEditing ? (
              <input type="number" step="0.01" value={form.target_length_m} onChange={(e) => handleChange('target_length_m', e.target.value)} placeholder={t('common:ui.optional')} />
            ) : (
              <div className="field-value">{formatNum(orderData?.target_length_m)}</div>
            )}
          </div>

          {/* Row 4: Temperature Range, Line Speed, Former Config */}
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.temperatureRange')}</label>
            {isEditing ? (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input type="number" step="0.01" value={form.temperature_min} onChange={(e) => handleChange('temperature_min', e.target.value)} placeholder={t('viewExtrusionWorkOrder.placeholders.min')} style={{ width: '45%' }} />
                <span>-</span>
                <input type="number" step="0.01" value={form.temperature_max} onChange={(e) => handleChange('temperature_max', e.target.value)} placeholder={t('viewExtrusionWorkOrder.placeholders.max')} style={{ width: '45%' }} />
              </div>
            ) : (
              <div className="field-value">
                {orderData?.temperature_min || orderData?.temperature_max
                  ? `${formatNum(orderData?.temperature_min)} - ${formatNum(orderData?.temperature_max)} °C`
                  : '-'}
              </div>
            )}
          </div>
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.lineSpeed')}</label>
            {isEditing ? (
              <input type="number" step="0.01" value={form.line_speed_mpm} onChange={(e) => handleChange('line_speed_mpm', e.target.value)} placeholder={t('common:ui.optional')} />
            ) : (
              <div className="field-value">{orderData?.line_speed_mpm ? `${formatNum(orderData?.line_speed_mpm)} m/min` : '-'}</div>
            )}
          </div>
          <div className="form-group">
            <label>{t('viewExtrusionWorkOrder.fields.formerConfig')}</label>
            {isEditing ? (
              <input type="text" value={form.former_config} onChange={(e) => handleChange('former_config', e.target.value)} placeholder={t('common:ui.optional')} />
            ) : (
              <div className="field-value">{orderData?.former_config || '-'}</div>
            )}
          </div>

          {/* Notes */}
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>{t('common:ui.notes')}</label>
            {isEditing ? (
              <textarea value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} rows={2} placeholder={t('viewExtrusionWorkOrder.placeholders.optionalNotes')} style={{ width: '100%', resize: 'vertical' }} />
            ) : (
              <div className="field-value">{orderData?.notes || '-'}</div>
            )}
          </div>

          {/* Audit info */}
          {!createMode && (
            <>
              <div className="form-group">
                <label>{t('viewExtrusionWorkOrder.fields.createdBy')}</label>
                <div className="field-value" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{orderData?.created_by} — {formatDateTime(orderData?.created_date)}</div>
              </div>
              {orderData?.updated_by && (
                <div className="form-group">
                  <label>{t('viewExtrusionWorkOrder.fields.updatedBy')}</label>
                  <div className="field-value" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{orderData?.updated_by} — {formatDateTime(orderData?.updated_date)}</div>
                </div>
              )}
              {isCompleted && orderData?.completed_by && (
                <div className="form-group">
                  <label>{t('viewExtrusionWorkOrder.fields.completedBy')}</label>
                  <div className="field-value" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{orderData?.completed_by} — {formatDateTime(orderData?.completed_date)}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* === MATERIALS TAB === */}
      {activeTab === 'materials' && !createMode && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>{t('viewExtrusionWorkOrder.materials.title')}</h3>
            {canAddMaterials && (
              <button className="btn-primary" onClick={() => setShowAddMaterial(true)}>
                + {t('viewExtrusionWorkOrder.materials.add')}
              </button>
            )}
          </div>

          {/* Add Material Form */}
          {showAddMaterial && (
            <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px', marginBottom: '12px', border: '1px solid var(--border-color)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', alignItems: 'end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('common:ui.material')}</label>
                  {renderSearchableInput(
                    'material',
                    materialSearchText,
                    setMaterialSearchText,
                    (id) => setMaterialForm(prev => ({ ...prev, inventory_item_id: id })),
                    t('viewExtrusionWorkOrder.placeholders.searchMaterial'),
                    inventoryItems
                  )}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('common:ui.type')}</label>
                  <select value={materialForm.material_type} onChange={(e) => setMaterialForm(prev => ({ ...prev, material_type: e.target.value }))}>
                    <option value="PELLET">{t('viewExtrusionWorkOrder.materialType.PELLET')}</option>
                    <option value="PIGMENT">{t('viewExtrusionWorkOrder.materialType.PIGMENT')}</option>
                    <option value="ADDITIVE">{t('viewExtrusionWorkOrder.materialType.ADDITIVE')}</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('viewExtrusionWorkOrder.fields.qtyKg')}</label>
                  <input type="number" step="0.001" value={materialForm.requested_quantity_kg} onChange={(e) => setMaterialForm(prev => ({ ...prev, requested_quantity_kg: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button className="btn-primary" onClick={handleAddMaterial}>{t('common:ui.add')}</button>
                  <button className="btn-secondary" onClick={() => { setShowAddMaterial(false); setMaterialSearchText(''); }}>{t('common:ui.cancel')}</button>
                </div>
              </div>
            </div>
          )}

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('common:ui.material')}</th>
                  <th>{t('common:ui.type')}</th>
                  <th>{t('viewExtrusionWorkOrder.columns.requested')}</th>
                  <th>{t('viewExtrusionWorkOrder.columns.approved')}</th>
                  <th>{t('viewExtrusionWorkOrder.columns.issued')}</th>
                  <th>{t('viewExtrusionWorkOrder.columns.available')}</th>
                  <th>{t('common:ui.status')}</th>
                  <th>{t('common:ui.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {materials.length === 0 ? (
                  <tr><td colSpan="8" className="no-data">{t('viewExtrusionWorkOrder.materials.empty')}</td></tr>
                ) : (
                  materials.map(m => (
                    <tr key={m.requisition_item_id}>
                      <td>
                        <strong>{m.item_code}</strong>
                        <br/><span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{m.item_name}</span>
                      </td>
                      <td>{t('viewExtrusionWorkOrder.materialType.' + m.material_type, { defaultValue: m.material_type })}</td>
                      <td>{formatNum(m.requested_quantity_kg, 3)} kg</td>
                      <td>{m.approved_quantity_kg != null ? `${formatNum(m.approved_quantity_kg, 3)} kg` : '-'}</td>
                      <td>{m.issued_quantity_kg != null && parseFloat(m.issued_quantity_kg) > 0 ? `${formatNum(m.issued_quantity_kg, 3)} kg` : '-'}</td>
                      <td>{formatNum(m.available_quantity, 3)} kg</td>
                      <td>{getMaterialStatusBadge(m.status)}</td>
                      <td>
                        {canManageMaterials && m.status === 'PENDING' && (
                          <button className="btn-small" onClick={() => handleApproveMaterial(m.requisition_item_id)} title={t('viewExtrusionWorkOrder.actions.approve')}>
                            {t('viewExtrusionWorkOrder.actions.approve')}
                          </button>
                        )}
                        {canManageMaterials && m.status === 'APPROVED' && (
                          <button className="btn-small" onClick={() => handleIssueMaterial(m.requisition_item_id)} title={t('viewExtrusionWorkOrder.actions.issue')}>
                            {t('viewExtrusionWorkOrder.actions.issue')}
                          </button>
                        )}
                        {canManageMaterials && (m.status === 'PENDING' || m.status === 'APPROVED') && (
                          <button className="btn-small btn-danger" onClick={() => handleCancelMaterial(m.requisition_item_id)} title={t('common:ui.cancel')}>
                            {t('common:ui.cancel')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === CONSUMPTION TAB === */}
      {activeTab === 'consumption' && !createMode && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>{t('viewExtrusionWorkOrder.consumption.title')}</h3>
            {canAddConsumption && (
              <button className="btn-primary" onClick={() => setShowAddConsumption(true)}>
                + {t('viewExtrusionWorkOrder.consumption.add')}
              </button>
            )}
          </div>

          {/* Add Consumption Form */}
          {showAddConsumption && (
            <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px', marginBottom: '12px', border: '1px solid var(--border-color)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '8px', alignItems: 'end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('common:ui.material')}</label>
                  {renderSearchableInput(
                    'consumption',
                    consumptionSearchText,
                    setConsumptionSearchText,
                    (id) => setConsumptionForm(prev => ({ ...prev, inventory_item_id: id })),
                    t('viewExtrusionWorkOrder.placeholders.searchMaterial'),
                    inventoryItems
                  )}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('common:ui.type')}</label>
                  <select value={consumptionForm.material_type} onChange={(e) => setConsumptionForm(prev => ({ ...prev, material_type: e.target.value }))}>
                    <option value="PELLET">{t('viewExtrusionWorkOrder.materialType.PELLET')}</option>
                    <option value="PIGMENT">{t('viewExtrusionWorkOrder.materialType.PIGMENT')}</option>
                    <option value="ADDITIVE">{t('viewExtrusionWorkOrder.materialType.ADDITIVE')}</option>
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('viewExtrusionWorkOrder.fields.bags25')}</label>
                  <input
                    type="number"
                    min="0"
                    value={consumptionForm.bags_used}
                    onChange={(e) => {
                      const bags = parseInt(e.target.value) || 0;
                      setConsumptionForm(prev => ({
                        ...prev,
                        bags_used: e.target.value,
                        kg_consumed: (bags * 25).toString(),
                      }));
                    }}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('viewExtrusionWorkOrder.fields.kgConsumed')}</label>
                  <input type="number" step="0.001" value={consumptionForm.kg_consumed} onChange={(e) => setConsumptionForm(prev => ({ ...prev, kg_consumed: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button className="btn-primary" onClick={handleAddConsumption}>{t('common:ui.add')}</button>
                  <button className="btn-secondary" onClick={() => { setShowAddConsumption(false); setConsumptionSearchText(''); }}>{t('common:ui.cancel')}</button>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '8px' }}>
                <label>{t('common:ui.notes')}</label>
                <input type="text" value={consumptionForm.notes} onChange={(e) => setConsumptionForm(prev => ({ ...prev, notes: e.target.value }))} placeholder={t('viewExtrusionWorkOrder.placeholders.optionalNotes')} style={{ width: '100%' }} />
              </div>
            </div>
          )}

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('common:ui.material')}</th>
                  <th>{t('common:ui.type')}</th>
                  <th>{t('viewExtrusionWorkOrder.columns.bags')}</th>
                  <th>{t('viewExtrusionWorkOrder.fields.kgConsumed')}</th>
                  <th>{t('viewExtrusionWorkOrder.columns.unitCost')}</th>
                  <th>{t('viewExtrusionWorkOrder.columns.recordedBy')}</th>
                  <th>{t('common:ui.date')}</th>
                  {canAddConsumption && <th>{t('common:ui.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {consumption.length === 0 ? (
                  <tr><td colSpan={canAddConsumption ? 8 : 7} className="no-data">{t('viewExtrusionWorkOrder.consumption.empty')}</td></tr>
                ) : (
                  consumption.map(c => (
                    <tr key={c.log_id}>
                      <td>
                        <strong>{c.item_code}</strong>
                        <br/><span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{c.item_name}</span>
                      </td>
                      <td>{t('viewExtrusionWorkOrder.materialType.' + c.material_type, { defaultValue: c.material_type })}</td>
                      <td>{c.bags_used || 0}</td>
                      <td>{formatNum(c.kg_consumed, 3)} kg</td>
                      <td>{formatCurrency(c.unit_cost)}</td>
                      <td>{c.recorded_by}</td>
                      <td>{formatDateTime(c.recorded_date)}</td>
                      {canAddConsumption && (
                        <td>
                          <button className="btn-small danger" onClick={() => handleDeleteConsumption(c.log_id)} title={t('common:ui.delete')}>
                            {t('common:ui.delete')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
                {consumption.length > 0 && (
                  <tr style={{ fontWeight: 'bold', background: 'var(--bg-secondary)' }}>
                    <td colSpan="3" style={{ textAlign: 'right' }}>{t('common:ui.total')}:</td>
                    <td>{formatNum(consumption.reduce((sum, c) => sum + parseFloat(c.kg_consumed || 0), 0), 3)} kg</td>
                    <td colSpan={canAddConsumption ? 4 : 3}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* === BOBBINS TAB === */}
      {activeTab === 'bobbins' && !createMode && (
        <div>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
            <SummaryCard
              label={t('viewExtrusionWorkOrder.summary.bobbinsProduced')}
              value={orderData?.bobbins_produced || 0}
              color={orderData?.bobbins_produced >= orderData?.planned_bobbins ? '#4caf50' : undefined}
            />
            <SummaryCard
              label={t('viewExtrusionWorkOrder.summary.goodKg')}
              value={formatNum(orderData?.good_kg_produced, 3)}
              unit="kg"
              color="#4caf50"
            />
            <SummaryCard
              label={t('viewExtrusionWorkOrder.summary.wasteKg')}
              value={formatNum(orderData?.total_waste_kg, 3)}
              unit="kg"
              color={parseFloat(orderData?.total_waste_kg || 0) > 0 ? '#f44336' : undefined}
            />
            <SummaryCard
              label={t('viewExtrusionWorkOrder.summary.wastePct')}
              value={formatNum(orderData?.waste_percentage, 2)}
              unit="%"
              color={parseFloat(orderData?.waste_percentage || 0) > 5 ? '#f44336' : '#4caf50'}
            />
          </div>

          {/* Bobbin Records */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>{t('viewExtrusionWorkOrder.bobbins.title')}</h3>
            {canAddBobbins && (
              <button className="btn-primary" onClick={() => setShowAddBobbin(true)}>
                + {t('viewExtrusionWorkOrder.bobbins.add')}
              </button>
            )}
          </div>

          {/* Add Bobbin Form */}
          {showAddBobbin && (
            <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px', marginBottom: '12px', border: '1px solid var(--border-color)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '8px', alignItems: 'end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('viewExtrusionWorkOrder.fields.barcodeLabel')}</label>
                  <input type="text" value={bobbinForm.bobbin_barcode} onChange={(e) => setBobbinForm(prev => ({ ...prev, bobbin_barcode: e.target.value }))} placeholder={t('common:ui.optional')} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('viewExtrusionWorkOrder.fields.meters')}</label>
                  <input type="number" step="0.01" value={bobbinForm.meters_produced} onChange={(e) => setBobbinForm(prev => ({ ...prev, meters_produced: e.target.value }))} placeholder={t('common:ui.optional')} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('viewExtrusionWorkOrder.fields.weightRequired')}</label>
                  <input type="number" step="0.001" value={bobbinForm.weight_kg} onChange={(e) => setBobbinForm(prev => ({ ...prev, weight_kg: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>{t('viewExtrusionWorkOrder.fields.classification')}</label>
                  <select value={bobbinForm.classification} onChange={(e) => setBobbinForm(prev => ({ ...prev, classification: e.target.value }))}>
                    <option value="GOOD">{t('viewExtrusionWorkOrder.classification.GOOD')}</option>
                    <option value="WASTE">{t('viewExtrusionWorkOrder.classification.WASTE')}</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button className="btn-primary" onClick={handleAddBobbin}>{t('common:ui.add')}</button>
                  <button className="btn-secondary" onClick={() => setShowAddBobbin(false)}>{t('common:ui.cancel')}</button>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: '8px' }}>
                <label>{t('common:ui.notes')}</label>
                <input type="text" value={bobbinForm.notes} onChange={(e) => setBobbinForm(prev => ({ ...prev, notes: e.target.value }))} placeholder={t('viewExtrusionWorkOrder.placeholders.optionalNotes')} style={{ width: '100%' }} />
              </div>
            </div>
          )}

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('viewExtrusionWorkOrder.columns.barcode')}</th>
                  <th>{t('viewExtrusionWorkOrder.fields.meters')}</th>
                  <th>{t('viewExtrusionWorkOrder.columns.weight')}</th>
                  <th>{t('viewExtrusionWorkOrder.fields.classification')}</th>
                  <th>{t('viewExtrusionWorkOrder.columns.recordedBy')}</th>
                  <th>{t('common:ui.date')}</th>
                  {canAddBobbins && <th>{t('common:ui.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {bobbins.length === 0 ? (
                  <tr><td colSpan={canAddBobbins ? 8 : 7} className="no-data">{t('viewExtrusionWorkOrder.bobbins.empty')}</td></tr>
                ) : (
                  bobbins.map(b => (
                    <tr key={b.bobbin_record_id}>
                      {editingBobbinId === b.bobbin_record_id ? (
                        <>
                          <td>{b.bobbin_sequence}</td>
                          <td><input type="text" value={editBobbinForm.bobbin_barcode} onChange={(e) => setEditBobbinForm(prev => ({ ...prev, bobbin_barcode: e.target.value }))} style={{ width: '80px' }} /></td>
                          <td><input type="number" step="0.01" value={editBobbinForm.meters_produced} onChange={(e) => setEditBobbinForm(prev => ({ ...prev, meters_produced: e.target.value }))} style={{ width: '70px' }} /></td>
                          <td><input type="number" step="0.001" value={editBobbinForm.weight_kg} onChange={(e) => setEditBobbinForm(prev => ({ ...prev, weight_kg: e.target.value }))} style={{ width: '70px' }} /></td>
                          <td>
                            <select value={editBobbinForm.classification} onChange={(e) => setEditBobbinForm(prev => ({ ...prev, classification: e.target.value }))} style={{ width: '80px' }}>
                              <option value="GOOD">{t('viewExtrusionWorkOrder.classification.GOOD')}</option>
                              <option value="WASTE">{t('viewExtrusionWorkOrder.classification.WASTE')}</option>
                            </select>
                          </td>
                          <td>{b.recorded_by}</td>
                          <td>{formatDateTime(b.recorded_date)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className="btn-small" onClick={handleSaveEditBobbin}>{t('common:ui.save')}</button>
                              <button className="btn-small" onClick={() => setEditingBobbinId(null)}>{t('common:ui.cancel')}</button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{b.bobbin_sequence}</td>
                          <td>{b.bobbin_barcode || '-'}</td>
                          <td>{b.meters_produced != null ? formatNum(b.meters_produced) : '-'}</td>
                          <td>{b.weight_kg != null ? formatNum(b.weight_kg, 3) : '-'}</td>
                          <td>
                            <span className={`status-badge ${b.classification === 'GOOD' ? 'status-success' : 'status-error'}`}>
                              {t('viewExtrusionWorkOrder.classification.' + b.classification, { defaultValue: b.classification })}
                            </span>
                          </td>
                          <td>{b.recorded_by}</td>
                          <td>{formatDateTime(b.recorded_date)}</td>
                          {canAddBobbins && (
                            <td>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button className="btn-small" onClick={() => handleStartEditBobbin(b)} title={t('common:ui.edit')}>{t('common:ui.edit')}</button>
                                <button className="btn-small danger" onClick={() => handleDeleteBobbin(b.bobbin_record_id)} title={t('common:ui.delete')}>{t('common:ui.delete')}</button>
                              </div>
                            </td>
                          )}
                        </>
                      )}
                    </tr>
                  ))
                )}
                {bobbins.length > 0 && (
                  <tr style={{ fontWeight: 'bold', background: 'var(--bg-secondary)' }}>
                    <td colSpan="3" style={{ textAlign: 'right' }}>{t('common:ui.total')}:</td>
                    <td>{formatNum(bobbins.reduce((sum, b) => sum + parseFloat(b.weight_kg || 0), 0), 3)} kg</td>
                    <td colSpan={canAddBobbins ? 4 : 3}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Waste Recording Section */}
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ margin: '0 0 12px 0' }}>{t('viewExtrusionWorkOrder.waste.title')}</h3>
            {orderData?.waste_recorded ? (
              <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '12px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('viewExtrusionWorkOrder.waste.total')}</label>
                    <div className="field-value">{formatNum(orderData?.total_waste_kg, 3)} kg</div>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('viewExtrusionWorkOrder.summary.wastePct')}</label>
                    <div className="field-value">{formatNum(orderData?.waste_percentage, 2)}%</div>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('common:ui.notes')}</label>
                    <div className="field-value">{orderData?.waste_notes || '-'}</div>
                  </div>
                </div>
                {canAddBobbins && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {t('viewExtrusionWorkOrder.waste.alreadyRecorded')}
                  </div>
                )}
              </div>
            ) : null}

            {canAddBobbins && (
              <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: '8px', marginTop: orderData?.waste_recorded ? '8px' : '0', border: '1px solid var(--border-color)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '8px', alignItems: 'end' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('viewExtrusionWorkOrder.waste.totalKg')}</label>
                    <input
                      type="number"
                      step="0.001"
                      value={wasteForm.total_waste_kg}
                      onChange={(e) => setWasteForm(prev => ({ ...prev, total_waste_kg: e.target.value }))}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('viewExtrusionWorkOrder.waste.notes')}</label>
                    <input
                      type="text"
                      value={wasteForm.waste_notes}
                      onChange={(e) => setWasteForm(prev => ({ ...prev, waste_notes: e.target.value }))}
                      placeholder={t('viewExtrusionWorkOrder.waste.notesPlaceholder')}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div>
                    <button className="btn-primary" onClick={handleRecordWaste}>
                      {orderData?.waste_recorded ? t('viewExtrusionWorkOrder.waste.update') : t('viewExtrusionWorkOrder.waste.record')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Inventory Entry Section (visible when COMPLETED) */}
          {isCompleted && (
            <div style={{ marginTop: '24px' }}>
              <h3 style={{ margin: '0 0 12px 0' }}>{t('viewExtrusionWorkOrder.entry.title')}</h3>

              {inventoryEntry && (
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  {/* Cost breakdown cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '16px' }}>
                    <SummaryCard label={t('viewExtrusionWorkOrder.summary.totalCost')} value={formatCurrency(inventoryEntry.calculated_cost)} color="#1976d2" />
                    <SummaryCard label={t('viewExtrusionWorkOrder.summary.costPerKg')} value={formatCurrency(Number(inventoryEntry.good_kg) > 0 ? inventoryEntry.calculated_cost / inventoryEntry.good_kg : 0)} color="#1976d2" />
                    <SummaryCard label={t('viewExtrusionWorkOrder.summary.costPerBobbin')} value={formatCurrency(inventoryEntry.cost_per_bobbin)} color="#1976d2" />
                    <SummaryCard label={t('viewExtrusionWorkOrder.summary.goodKg')} value={formatNum(inventoryEntry.good_kg, 3)} unit="kg" color="#4caf50" />
                    <SummaryCard label={t('viewExtrusionWorkOrder.tabs.bobbins')} value={inventoryEntry.bobbins_entered} color="#4caf50" />
                  </div>

                  {/* Entry details */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>{t('common:ui.status')}</label>
                      <div className="field-value">
                        <span className={`status-badge ${
                          inventoryEntry.status === 'APPROVED' ? 'status-success' :
                          inventoryEntry.status === 'REJECTED' ? 'status-error' : 'status-warning'
                        }`}>
                          {t('viewExtrusionWorkOrder.entryStatus.' + inventoryEntry.status, { defaultValue: inventoryEntry.status })}
                        </span>
                      </div>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>{t('viewExtrusionWorkOrder.entry.destination')}</label>
                      <div className="field-value">{inventoryEntry.destination_location}</div>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>{t('viewExtrusionWorkOrder.entry.requestedBy')}</label>
                      <div className="field-value" style={{ fontSize: '12px' }}>
                        {inventoryEntry.requested_by} — {formatDateTime(inventoryEntry.requested_date)}
                      </div>
                    </div>
                    {inventoryEntry.approved_by && (
                      <div className="form-group" style={{ margin: 0 }}>
                        <label>{inventoryEntry.status === 'REJECTED' ? t('viewExtrusionWorkOrder.entry.rejectedBy') : t('viewExtrusionWorkOrder.entry.approvedBy')}</label>
                        <div className="field-value" style={{ fontSize: '12px' }}>
                          {inventoryEntry.approved_by} — {formatDateTime(inventoryEntry.approved_date)}
                        </div>
                      </div>
                    )}
                  </div>

                  {inventoryEntry.notes && (
                    <div className="form-group" style={{ margin: '0 0 12px 0' }}>
                      <label>{t('common:ui.notes')}</label>
                      <div className="field-value">{inventoryEntry.notes}</div>
                    </div>
                  )}

                  {inventoryEntry.rejection_reason && (
                    <div style={{ padding: '8px 12px', background: '#ffebee', borderRadius: '6px', border: '1px solid #ef9a9a', marginBottom: '12px' }}>
                      <strong style={{ color: '#d32f2f' }}>{t('viewExtrusionWorkOrder.entry.rejectionReason')}:</strong> {inventoryEntry.rejection_reason}
                    </div>
                  )}

                  {/* Approve / Reject buttons */}
                  {canManageInventoryEntry && inventoryEntry.status === 'PENDING' && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
                      <button className="btn-primary" onClick={() => handleApproveInventoryEntry(inventoryEntry.entry_id)}>
                        {t('viewExtrusionWorkOrder.actions.approve')}
                      </button>
                      {!showRejectForm ? (
                        <button className="btn-danger" onClick={() => setShowRejectForm(true)}>
                          {t('viewExtrusionWorkOrder.actions.reject')}
                        </button>
                      ) : (
                        <div style={{ display: 'flex', gap: '4px', flex: 1, alignItems: 'end' }}>
                          <input
                            type="text"
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder={t('viewExtrusionWorkOrder.entry.rejectionReasonPlaceholder')}
                            style={{ flex: 1 }}
                          />
                          <button className="btn-danger" onClick={() => handleRejectInventoryEntry(inventoryEntry.entry_id)}>
                            {t('viewExtrusionWorkOrder.actions.confirmReject')}
                          </button>
                          <button className="btn-secondary" onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}>
                            {t('common:ui.cancel')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {(!inventoryEntry || inventoryEntry.status === 'REJECTED') && (
                /* No entry yet, or the last one was rejected — show request form */
                <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px', border: '1px solid var(--border-color)', marginTop: inventoryEntry ? '12px' : 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '8px', alignItems: 'end' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>{t('viewExtrusionWorkOrder.entry.destinationLocation')}</label>
                      <input
                        type="text"
                        value={inventoryEntryForm.destination_location}
                        onChange={(e) => setInventoryEntryForm(prev => ({ ...prev, destination_location: e.target.value }))}
                        placeholder={t('viewExtrusionWorkOrder.entry.destinationPlaceholder')}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>{t('common:ui.notes')}</label>
                      <input
                        type="text"
                        value={inventoryEntryForm.notes}
                        onChange={(e) => setInventoryEntryForm(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder={t('viewExtrusionWorkOrder.placeholders.optionalNotes')}
                        style={{ width: '100%' }}
                      />
                    </div>
                    <div>
                      <button className="btn-primary" onClick={handleRequestInventoryEntry}>
                        {t('viewExtrusionWorkOrder.entry.request')}
                      </button>
                    </div>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {t('viewExtrusionWorkOrder.entry.hint')}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Void info */}
      {status === 'VOIDED' && orderData?.void_reason && (
        <div style={{ marginTop: '12px', padding: '12px 16px', background: '#ffebee', borderRadius: '8px', border: '1px solid #ef9a9a' }}>
          <strong style={{ color: '#d32f2f' }}>{t('viewExtrusionWorkOrder.void.voided')}:</strong> {orderData.void_reason}
          <br/><span style={{ fontSize: '12px', color: '#777' }}>{t('viewExtrusionWorkOrder.void.byOn', { user: orderData.voided_by, date: formatDateTime(orderData.voided_date) })}</span>
        </div>
      )}

      {/* Void Confirmation Modal */}
      {showVoidModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: 'var(--bg-primary, #fff)', borderRadius: '12px', padding: '24px',
            width: '480px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}>
            <h3 style={{ margin: '0 0 12px', color: '#d32f2f' }}>{t('viewExtrusionWorkOrder.void.title')}</h3>
            <p style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
              {t('viewExtrusionWorkOrder.void.intro')}
            </p>
            <ul style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-secondary)', paddingLeft: '20px' }}>
              <li>{t('viewExtrusionWorkOrder.void.issuedReturned')}</li>
              <li>{t('viewExtrusionWorkOrder.void.linesCancelled')}</li>
            </ul>
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label>{t('viewExtrusionWorkOrder.void.reasonLabel')}</label>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder={t('viewExtrusionWorkOrder.void.reasonPlaceholder')}
                rows={3}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={() => { setShowVoidModal(false); setVoidReason(''); }}
                disabled={voiding}
              >
                {t('common:ui.cancel')}
              </button>
              <button
                className="btn-danger"
                onClick={handleVoid}
                disabled={voiding || voidReason.trim().length < 5}
              >
                {voiding ? t('viewExtrusionWorkOrder.void.voiding') : t('viewExtrusionWorkOrder.void.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ViewExtrusionWorkOrder;
